/**
 * Suggestions « Complétez avec » du panier en tiroir.
 *
 * - Demande à Shopify les produits complémentaires du dernier article ajouté
 *   (/recommendations/products, rendu par la section kl-cart-suggestions) ; la section se replie
 *   sur la collection choisie dans les réglages du thème s'il n'y en a aucun.
 * - Le tiroir est remplacé par le thème à chaque mise à jour du panier : l'élément se recharge
 *   quand son contenu est vidé, avec un cache pour éviter tout clignotement.
 * - « Ajouter » ajoute la variante au panier puis déclenche l'événement `cart:update` du thème,
 *   qui met à jour le tiroir et la pastille du panier.
 * Aucune dépendance.
 */
(() => {
  if (customElements.get('kl-cart-suggestions')) return;

  const SECTION = 'kl-cart-suggestions';
  /** @type {Map<string, string>} */
  const cache = new Map();

  const root = () => window.Theme?.routes?.root ?? '/';

  class KlCartSuggestions extends HTMLElement {
    /** @type {MutationObserver | undefined} */
    #observer;
    #loading = false;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.#observer = new MutationObserver(() => {
        if (!this.childElementCount) this.#load();
      });
      this.#observer.observe(this, { childList: true });
      if (!this.childElementCount) this.#load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.#observer?.disconnect();
    }

    get #key() {
      return `${this.dataset.productId}|${this.dataset.signature}`;
    }

    async #load() {
      const productId = this.dataset.productId;
      if (!productId || this.#loading) return;

      const key = this.#key;
      const cached = cache.get(key);
      if (cached !== undefined) {
        this.#render(cached);
        return;
      }

      this.#loading = true;
      try {
        const limit = this.dataset.limit || '8';
        let html = await this.#fetchSection(
          `${root()}recommendations/products?section_id=${SECTION}&product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=complementary`
        );
        // Repli : si le point d'accès des recommandations échoue, la section choisit seule la collection de secours.
        if (html === null) html = await this.#fetchSection(`${root()}?section_id=${SECTION}`);
        const content = html ?? '<div class="kl-sugg" data-count="0"></div>';
        cache.set(key, content);
        this.#render(content);
      } catch (error) {
        console.error('Suggestions du panier indisponibles :', error);
        this.#render('<div class="kl-sugg" data-count="0"></div>');
      } finally {
        this.#loading = false;
      }
    }

    /** @param {string} url @returns {Promise<string | null>} */
    async #fetchSection(url) {
      const response = await fetch(url);
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const block = doc.querySelector('[data-kl-sugg]');
      return block ? block.outerHTML : null;
    }

    /** @param {string} html */
    #render(html) {
      this.innerHTML = html;
      this.toggleAttribute('data-empty', this.querySelector('[data-kl-sugg]')?.getAttribute('data-count') === '0');
    }

    /** @param {MouseEvent} event */
    #onClick = async (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-kl-sugg-add]') : null;
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      const errorBox = this.querySelector('.kl-sugg__error');
      const label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (errorBox instanceof HTMLElement) errorBox.hidden = true;

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
        .filter(Boolean);

      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
        });
        const data = await response.json();

        if (data.status || data.message) {
          throw new Error(data.description || data.message);
        }

        // Nombre d'articles lu dans le tiroir mis à jour (comme le fait le thème), pour la pastille du panier.
        let itemCount = 0;
        const html = data.sections?.[sectionIds[0]];
        if (html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          itemCount = parseInt(doc.querySelector('[ref="cartItemCount"]')?.textContent ?? '0', 10) || 0;
        }

        const update = new Event('cart:update', { bubbles: true });
        update.detail = {
          resource: {},
          sourceId: String(variantId),
          data: { source: 'kl-cart-suggestions', itemCount, variantId: String(variantId), sections: data.sections },
        };
        this.dispatchEvent(update);
      } catch (error) {
        console.error(error);
        if (errorBox instanceof HTMLElement) {
          errorBox.textContent = error instanceof Error && error.message ? error.message : "Impossible d'ajouter ce produit.";
          errorBox.hidden = false;
        }
        button.textContent = label;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };
  }

  customElements.define('kl-cart-suggestions', KlCartSuggestions);
})();
